import type { Uuid } from "@/domains/global/identifiers";
import { logger } from "@/modules/logger";
import { getCurrentAssistantId } from "@/store/assistant/assistant-id";
import type { AppStore } from "@/store/AppStore";
import { liveQuery } from "dexie";
import type { Subscription } from "dexie";
import {
  action,
  computed,
  makeObservable,
  observable,
  onBecomeObserved,
  onBecomeUnobserved,
  reaction,
  runInAction,
} from "mobx";
import type { IReactionDisposer } from "mobx";

export interface CurrentAssistantQueryState<Data> {
  assistantId: Uuid | undefined;
  data: Data;
  isError: boolean;
  isLoading: boolean;
}

interface CurrentAssistantQueryResult<Data> {
  assistantId: Uuid | undefined;
  data: Data;
}

/**
 * Runs a Dexie query for the current assistant while its state is observed.
 *
 * Synced assistant settings retrigger the Dexie live query. The MobX reaction
 * handles API-backed assistant identity changes that do not write settings.
 */
export class CurrentAssistantQueryObservable<Data> {
  private readonly createEmptyData: () => Data;
  private readonly query: ({ assistantId }: { assistantId: Uuid }) => Promise<Data>;
  private readonly store: AppStore;

  private assistantIdentityReaction: IReactionDisposer | undefined;
  private liveQuerySubscription: Subscription | undefined;

  private assistantId: Uuid | undefined;
  private data: Data;
  private isError = false;
  private isLoading = true;

  constructor({
    createEmptyData,
    query,
    store,
  }: {
    createEmptyData: () => Data;
    query: ({ assistantId }: { assistantId: Uuid }) => Promise<Data>;
    store: AppStore;
  }) {
    this.createEmptyData = createEmptyData;
    this.query = query;
    this.store = store;
    this.data = createEmptyData();

    makeObservable<
      this,
      | "assistantId"
      | "assistantIdentityReaction"
      | "createEmptyData"
      | "data"
      | "isError"
      | "isLoading"
      | "liveQuerySubscription"
      | "query"
      | "restartLiveQuery"
      | "start"
      | "store"
      | "stop"
    >(this, {
      assistantId: observable,
      assistantIdentityReaction: false,
      createEmptyData: false,
      data: observable,
      isError: observable,
      isLoading: observable,
      liveQuerySubscription: false,
      query: false,
      restartLiveQuery: action,
      start: action,
      state: computed,
      store: false,
      stop: action,
    });

    onBecomeObserved(this, "state", () => this.start());
    onBecomeUnobserved(this, "state", () => this.stop());
  }

  get state(): CurrentAssistantQueryState<Data> {
    return {
      assistantId: this.assistantId,
      data: this.data,
      isError: this.isError,
      isLoading: this.isLoading,
    };
  }

  private start() {
    this.assistantIdentityReaction?.();
    this.assistantIdentityReaction = reaction(
      () => ({
        assistantId: this.store.memAgent.currentAssistant?.id,
        isReady: this.store.memAgent.isReady,
        personalSpaceAccountId: this.store.spaceAccounts.myPersonalSpaceAccountId,
      }),
      () => this.restartLiveQuery(),
      { fireImmediately: true }
    );
  }

  private restartLiveQuery() {
    this.liveQuerySubscription?.unsubscribe();
    this.assistantId = undefined;
    this.data = this.createEmptyData();
    this.isError = false;
    this.isLoading = true;

    this.liveQuerySubscription = liveQuery(async (): Promise<CurrentAssistantQueryResult<Data>> => {
      const assistantId = await getCurrentAssistantId({ store: this.store });

      if (!assistantId) {
        return {
          assistantId: undefined,
          data: this.createEmptyData(),
        };
      }

      return {
        assistantId,
        data: await this.query({ assistantId }),
      };
    }).subscribe({
      error: error => {
        logger.error({
          message: "Failed to query current assistant models",
          info: { error: String(error) },
        });

        runInAction(() => {
          this.assistantId = undefined;
          this.data = this.createEmptyData();
          this.isError = true;
          this.isLoading = false;
        });
      },
      next: result => {
        runInAction(() => {
          this.assistantId = result.assistantId;
          this.data = result.data;
          this.isError = false;
          this.isLoading = result.assistantId === undefined && !this.store.memAgent.isReady;
        });
      },
    });
  }

  private stop() {
    this.assistantIdentityReaction?.();
    this.liveQuerySubscription?.unsubscribe();
    this.assistantIdentityReaction = undefined;
    this.liveQuerySubscription = undefined;
    this.assistantId = undefined;
    this.data = this.createEmptyData();
    this.isError = false;
    this.isLoading = true;
  }
}
